import * as React from "react";
import { Link, useLocation, useParams } from "react-router";
import { FolderKanban } from "lucide-react";

import { useSession } from "@/lib/auth-client";
import { CHEETAH_LOGO_SRC } from "@/lib/brand-assets";
import { trpc } from "@/lib/trpc";
import { NavUser } from "@/components/nav-user";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import {
  buildCampaignDetailPath,
  buildPlannedPostDetailPath,
} from "@/components/campaign-review/utils";
import { ADMIN_NAV_GROUPS, getAdminNavItemActiveState } from "@/components/app-sidebar-nav";
import { formatShortDate } from "@/lib/date-format";

type SidebarScheduledPost = {
  id: string;
  scheduledFor: string;
  slot: { concept: string };
};

const collapsedActiveChildMenuButtonClassName =
  "group-data-[collapsible=icon]:bg-sidebar-primary group-data-[collapsible=icon]:text-sidebar-primary-foreground group-data-[collapsible=icon]:hover:bg-sidebar-primary group-data-[collapsible=icon]:hover:text-sidebar-primary-foreground group-data-[collapsible=icon]:[&>svg]:text-sidebar-primary-foreground";

export const getCampaignSidebarPosts = <T extends SidebarScheduledPost>(
  posts: T[],
  activePostId?: string,
): T[] => {
  const activePost = posts.find((post) => post.id === activePostId);
  return activePost ? [activePost] : [];
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const {
    id: brandId,
    campaignId,
    postId,
  } = useParams<{
    id: string;
    campaignId: string;
    postId: string;
  }>();
  const { data: session } = useSession();
  const user = session?.user;
  const isCampaignRoute = Boolean(
    brandId && campaignId && location.pathname.includes("/campaigns/"),
  );
  const campaignQuery = trpc.admin.campaigns.get.useQuery(
    { brandId: brandId ?? "", campaignId: campaignId ?? "" },
    { enabled: isCampaignRoute, staleTime: 30_000 },
  );
  const campaign = campaignQuery.data;
  const campaignPath = isCampaignRoute
    ? buildCampaignDetailPath(brandId ?? "", campaignId ?? "")
    : null;
  const sidebarPosts = campaign ? getCampaignSidebarPosts(campaign.scheduledPosts, postId) : [];

  const compactPostLabel = (scheduledFor: string, concept: string) => {
    const compactConcept = concept.length > 32 ? `${concept.slice(0, 29)}...` : concept;
    return `${formatShortDate(scheduledFor)} · ${compactConcept}`;
  };

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild tooltip="Cheetah Admin">
              <Link to="/brands">
                <img src={CHEETAH_LOGO_SRC} alt="Cheetah" className="h-4 w-auto" />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {ADMIN_NAV_GROUPS.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const activeState = getAdminNavItemActiveState(location.pathname, item);
                  const Icon = item.icon;
                  return (
                    <SidebarMenuItem key={item.href}>
                      <SidebarMenuButton
                        asChild
                        isActive={activeState.isActive}
                        className={
                          activeState.activeChildHref
                            ? collapsedActiveChildMenuButtonClassName
                            : undefined
                        }
                        tooltip={item.label}
                      >
                        <Link to={item.href}>
                          <Icon />
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                      {item.children ? (
                        <SidebarMenuSub>
                          {item.children.map((child) => (
                            <SidebarMenuSubItem key={child.href}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={activeState.activeChildHref === child.href}
                              >
                                <Link to={child.href}>{child.label}</Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      ) : null}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}

        {campaign && campaignPath ? (
          <SidebarGroup>
            <SidebarGroupLabel>{campaign.name}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === campaignPath}
                    tooltip="Campaign Board"
                  >
                    <Link to={campaignPath}>
                      <FolderKanban />
                      <span>Campaign Board</span>
                    </Link>
                  </SidebarMenuButton>
                  <SidebarMenuSub>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton asChild isActive={location.pathname === campaignPath}>
                        <Link to={campaignPath}>Review queue</Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    {sidebarPosts.map((post) => {
                      const href = buildPlannedPostDetailPath(
                        brandId ?? "",
                        campaignId ?? "",
                        post.id,
                      );
                      const isActive = postId === post.id;

                      return (
                        <SidebarMenuSubItem key={post.id}>
                          <SidebarMenuSubButton asChild isActive={isActive}>
                            <Link to={href}>
                              <span>{compactPostLabel(post.scheduledFor, post.slot.concept)}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      );
                    })}
                  </SidebarMenuSub>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}
      </SidebarContent>

      <SidebarFooter>
        <NavUser
          user={{
            name: user?.name ?? user?.email.split("@")[0] ?? "Admin",
            email: user?.email ?? "",
            avatar: user?.image ?? "",
          }}
        />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
